import React, { useEffect, useState, useRef } from "react"
import "./ViewCommonCourse.scss"
import { Button, Card, CardBody, Col, Row } from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { InputText } from "primereact/inputtext"
import { InputTextarea } from "primereact/inputtextarea"
import { useParams, useNavigate } from "react-router-dom"
import CommonCourseApi from "Api/CommonCourseApi"
import { Toast } from 'primereact/toast'
import { Avatar } from "primereact/avatar"

const ViewCommonCourse = () => {
  document.title = "View Common Course"
  const toast = useRef(null)
  const accessToken = localStorage.getItem("accessToken")
  const navigate = useNavigate()
  const { id } = useParams()
  const [courseData, setCourseData] = useState({})

  const breadcrumbItems = [
    { label: "Pixwik", link: "#" },
    { label: "Common Courses", link: "#" },
    { label: "View Common Course", link: "#" },
  ]

  const getCommonCourse = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    CommonCourseApi.getCommonCourseById(id, config)
      .then(res => {
        if (res.data.status) {
          setCourseData(res.data.data)
        } else {
          toast.current.show({
            severity: 'error',
            summary: 'Error',
            detail: res.data.message || 'Failed to fetch common course details',
            life: 3000
          })
        }
      })
      .catch(error => {
        console.error("Error fetching common course:", error)
        if (error.response?.status === 404) {
          toast.current.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Common course not found',
            life: 3000
          })
        } else if (error.response?.status === 400) {
          toast.current.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.message || 'Invalid course ID format',
            life: 3000
          })
        } else {
          toast.current.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.message || 'Failed to fetch common course details',
            life: 3000
          })
        }
      })
  }

  useEffect(() => {
    if (id) {
      getCommonCourse()
    }
  }, [id])

  return (
    <div className="view-common-course">
      <Toast ref={toast} />
      <Row>
        <Col xs="12">
          <div className="common-course-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb data={breadcrumbItems} pageTitle="View Common Course" />
          </div>

          <Card className="mt-4">
            <CardBody>
              <div className="common-course-view-form">
                <h5 className="mb-4">Basic Information</h5>
                <div className="form-group mb-3">
                  <label htmlFor="courseName">Course Name</label>
                  <InputText
                    id="courseName"
                    value={courseData.courseName}
                    className="form-control"
                    disabled
                  />
                </div>

                <h5 className="mb-4 mt-4">Course Overview</h5>
                <div className="form-group mb-3">
                  <label htmlFor="overview">Overview</label>
                  <InputText
                    id="overview"
                    value={courseData.courseOverview?.overview}
                    className="form-control"
                    disabled
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="courseHighlights">Course Highlights</label>
                  <InputText
                    id="courseHighlights"
                    value={courseData.courseOverview?.courseHighlights}
                    className="form-control"
                    disabled
                  />
                </div>

                <h5 className="mb-4 mt-4">Career Outcomes</h5>
                <div className="form-group mb-3">
                  <label htmlFor="careerOutcome">Career Outcome</label>
                  <InputText
                    id="careerOutcome"
                    value={courseData.courseCareerOutcome?.careerOutcome}
                    className="form-control"
                    disabled
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="jobRoles">Job Roles</label>
                  <InputText
                    id="jobRoles"
                    value={courseData.courseCareerOutcome?.jobRoles}
                    className="form-control"
                    disabled
                  />
                </div>

                <h5 className="mb-4 mt-4">Course Requirements</h5>
                <div className="form-group mb-3">
                  <label htmlFor="eligibility">Eligibility</label>
                  <InputText
                    id="eligibility"
                    value={courseData.courseRequirements?.eligibility}
                    className="form-control"
                    disabled
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="medicalAdmission">Medical Admission</label>
                  <InputText
                    id="medicalAdmission"
                    value={courseData.courseRequirements?.medicalAdmission}
                    className="form-control"
                    disabled
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="admissionRequirements">Admission Requirements</label>
                  <InputText
                    id="admissionRequirements"
                    value={courseData.courseRequirements?.admissionRequirements}
                    className="form-control"
                    disabled
                  />
                </div>

                {courseData.courseExam?.length > 0 && (
                  <>
                    <h5 className="mb-4 mt-4">Exams</h5>
                    <div className="exams-section">
                      <Row className="g-4">
                        {courseData.courseExam.map((exam, index) => (
                          <Col md="6" lg="4" key={index}>
                            <Card className="exam-card h-100 p-3">
                              <div className="exam-header d-flex align-items-center gap-3 mb-3">
                                <Avatar 
                                  label={`E${index + 1}`}
                                  size="large" 
                                  shape="circle"
                                  style={{ 
                                    backgroundColor: '#' + Math.floor(Math.random()*16777215).toString(16),
                                    color: '#ffffff',
                                    padding: '10px'
                                  }}
                                />
                                <div>
                                  <h6 className="mb-1">{exam.examName}</h6>
                                  <small className="text-muted">
                                    {new Date(exam.examDate).toLocaleDateString()}
                                  </small>
                                </div>
                              </div>
                              <div className="exam-details">
                                <table className="table table-borderless mb-0">
                                  <tbody>
                                    <tr>
                                      <td><strong>Duration:</strong></td>
                                      <td>{exam.examDuration}</td>
                                    </tr>
                                    <tr>
                                      <td><strong>Location:</strong></td>
                                      <td>{exam.examLocation || "N/A"}</td>
                                    </tr>
                                    <tr>
                                      <td><strong>Registration:</strong></td>
                                      <td>{exam.examRegistration}</td>
                                    </tr>
                                    <tr>
                                      <td><strong>Fee:</strong></td>
                                      <td>{exam.examFee}</td>
                                    </tr>
                                  </tbody>
                                </table>
                                <div className="exam-description mt-3">
                                  <p className="mb-0" style={{ lineHeight: '1.6' }}>
                                    {exam.examDescription}
                                  </p>
                                </div>
                              </div>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </>
                )}

                {courseData.faq?.length > 0 && (
                  <>
                    <h5 className="mb-4 mt-4">FAQs</h5>
                    <div className="faqs-section">
                      <Row className="g-4">
                        {courseData.faq.map((faq, index) => (
                          <Col md="6" lg="4" key={index}>
                            <Card className="faq-card h-100 p-3">
                              <div className="faq-header d-flex align-items-center gap-3 mb-3">
                                <Avatar 
                                  label={`Q${index + 1}`}
                                  size="large" 
                                  shape="circle"
                                  style={{ 
                                    backgroundColor: '#' + Math.floor(Math.random()*16777215).toString(16),
                                    color: '#ffffff',
                                    padding: '10px'
                                  }}
                                />
                                <div>
                                  <h6 className="mb-1">FAQ {index + 1}</h6>
                                </div>
                              </div>
                              <div className="faq-content">
                                <div className="question mb-3">
                                  <strong>Question:</strong>
                                  <p className="mb-0 mt-2" style={{ lineHeight: '1.6' }}>
                                    {faq.question}
                                  </p>
                                </div>
                                <div className="answer">
                                  <strong>Answer:</strong>
                                  <p className="mb-0 mt-2" style={{ lineHeight: '1.6' }}>
                                    {faq.answer}
                                  </p>
                                </div>
                              </div>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </>
                )}

                <div className="d-flex justify-content-start mt-4">
                  <Button
                    type="button"
                    color="secondary"
                    className="waves-effect"
                    onClick={() => navigate("/commonCourseList")}
                  >
                    Back
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default ViewCommonCourse 